import type { ReactNode } from "react";
import { createContext } from "react";

interface ToastItem {
	id: string;
	message: string;
	type: "success" | "error" | "warning" | "info";
}

interface ToastContextValue {
	success: (message: string) => void;
	error: (message: string) => void;
	warning: (message: string) => void;
	info: (message: string) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error("useToast precisa estar dentro de ToastProvider");
	return ctx;
}

const TOAST_CONFIG = {
	success: { icon: "ri-checkbox-circle-fill", bg: "bg-green-600" },
	error: { icon: "ri-error-warning-fill", bg: "bg-red-600" },
	warning: { icon: "ri-alert-fill", bg: "bg-amber-500" },
	info: { icon: "ri-information-fill", bg: "bg-blue-600" },
} as const;

const DURATION = 4000;

export default function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<ToastItem[]>([]);

	const dismiss = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const add = useCallback(
		(message: string, type: ToastItem["type"]) => {
			const id = Math.random().toString(36).slice(2, 9);
			// Mantém no máximo 5 toasts empilhados
			setToasts((prev) => [...prev.slice(-4), { id, message, type }]);
			setTimeout(() => dismiss(id), DURATION);
		},
		[dismiss],
	);

	const value = useMemo<ToastContextValue>(
		() => ({
			success: (msg) => add(msg, "success"),
			error: (msg) => add(msg, "error"),
			warning: (msg) => add(msg, "warning"),
			info: (msg) => add(msg, "info"),
		}),
		[add],
	);

	return (
		<ToastContext.Provider value={value}>
			{children}
			{/* Renderiza toasts em posição fixa, acima de tudo */}
			<div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
				{toasts.map((t) => {
					const cfg = TOAST_CONFIG[t.type];
					return (
						<div
							key={t.id}
							className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-xl pointer-events-auto animate-slide-in ${cfg.bg} text-white`}
						>
							<i className={`${cfg.icon} text-lg mt-0.5 flex-shrink-0`} />
							<p className="text-sm font-medium flex-1 leading-snug">{t.message}</p>
							<button
								type="button"
								onClick={() => dismiss(t.id)}
								className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity ml-1"
							>
								<i className="ri-close-line text-base" />
							</button>
						</div>
					);
				})}
			</div>
		</ToastContext.Provider>
	);
}
